import {ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Router} from '@angular/router';
import {RequestError, Store} from '@softline/core';
import {Observable} from 'rxjs';
import {CouponType} from '../../data/coupon-type';
import {
  MessageBarStore,
  ModalStore,
  QuestionResult,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL} from '@softline/ui-core';
import {ConfirmDialogComponent} from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {BackNavigable, BackNavigationService, showRequestErrors} from '@softline/application';
import {SOFTLINE_FEATURE_COUPON} from '../../coupon-app.shared';
import {CouponStore} from '../../store/coupon.store';
import {CommonModule} from '@angular/common';

import { ChipComponent } from '@softline/ui-core';
import { ChipGroupComponent } from '@softline/ui-core';
import { FilterPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { ValidationMessageComponent } from '@softline/ui-core';
@Component({
    selector: 'lib-redeem-coupon',
    imports: [CommonModule,
    ChipComponent,
    ChipGroupComponent,
    FilterPipe,
    TranslatePipe,
    ValidationMessageComponent],
    templateUrl: './redeem-coupon.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./redeem-coupon.component.css']
})
export class RedeemCouponComponent implements OnInit, OnDestroy, BackNavigable {
  amounts: number[] = [10, 20, 50];
  selectedIndex?: number;
  selectedAmount = 0;

  coupon$: Observable<CouponType> = this.store.observe(SOFTLINE_FEATURE_COUPON, CouponStore.getters.data);

  code?: number;

  constructor(protected router: Router,
              private store: Store,
              private cd: ChangeDetectorRef,
              private backNavigationService: BackNavigationService) {
  }

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);
    const entity = this.store.get(SOFTLINE_FEATURE_COUPON, CouponStore.getters.data);

    if (!entity) {
      await this.router.navigate(['/gutscheinkarten']);
      return;
    }

    this.code = entity.code;
  }

  ngOnDestroy() {
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/gutscheinkarten', 'existing']);
  }

  select(index: number, availableAmount: number): void {
    if (availableAmount < this.amounts[index]) {
      this.selectedIndex = this.amounts.length;
      this.selectedAmount = availableAmount;
      return;
    }

    if (!this.selectedIndex) {
      this.selectedIndex = index;
      this.selectedAmount = this.amounts[index];
      return;
    }

    if (this.selectedIndex && this.selectedIndex !== index) {
      this.selectedIndex = index;
      this.selectedAmount = availableAmount >= this.amounts[index] ? this.amounts[index] : availableAmount;
    }
  }

  selectAvailable(availableAmount: number): void {
    this.selectedIndex = this.amounts.length;
    this.selectedAmount = availableAmount;
  }

  setAmount(inputValue: number, availableAmount: number) {
    this.selectedAmount = inputValue > availableAmount ? availableAmount : inputValue;
  }

  resetChips(): void {
    this.selectedIndex = undefined;
  }

  async redeem(): Promise<void> {
    if (!this.code) {
      await this.router.navigate(['/gutscheinkarten']);
      return;
    }

    const result: QuestionResult | 'YES' | 'NO' | 'CANCEL' | 'DISMISSED' = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
        id: 'CONFIRM_DIALOG',
        component: ConfirmDialogComponent,
        data: { amount: this.selectedAmount, type: 'redeem' },
        dismiss: { escape: false, backdrop: false, button: true }
    });
    if (result === 'NO' || result === 'CANCEL' || result === 'DISMISSED') { return; }
    await this.handleResult(result);
  }

  private async  handleResult(result: QuestionResult): Promise<void> {
    if (result !== 'YES') {
      return;
    }
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_COUPON, CouponStore.actions.transaction, {amount: this.selectedAmount, action: 'DEBIT', code: this.code}
      );
      await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.success, {
        message: '#COUPONS.SUCCESS_REDEEM',
        duration: 5000
      });
      await this.router.navigate(['/gutscheinkarten']);
    } catch (e: any) {
      if (e instanceof RequestError) {
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, {
          message: '#COUPONS.GENERIC_ERROR',
          duration: 3000
        });
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  amountFilter(amount: number, balance: number | undefined): boolean {
    return amount <= (balance ?? Number.MIN_VALUE);
  }

  async cancelRedeem(): Promise<void> {
    await this.router.navigate(['/gutscheinkarten/existing']);
  }
}
